import { useRequiredContext } from "@redotech/react-util/context";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { FulfillmentGroupStatus } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import {
  summaryPackages,
  UnclaimableReason,
} from "@redotech/redo-fulfillment-group-common/shipment-summary";
import { Provider } from "@redotech/redo-order-common/provider";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { useMemo } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { getEffectiveShipmentSummary } from "./effective-shipment-summary";

const CANCELABLE_ORDER_PROVIDERS = new Set<Provider>([
  Provider.SHOPIFY,
  Provider.BIGCOMMERCE,
  Provider.WALMART,
  Provider.MANUAL,
  Provider.REDO,
  Provider.REDO_MARKETPLACE,
  Provider.LINNWORKS_DIRECT,
]);

export function canCancelFulfillmentGroups(
  fulfillmentGroups: readonly Pick<
    FulfillmentGroupData,
    "providers" | "status"
  >[],
): boolean {
  return (
    fulfillmentGroups.length === 1 &&
    fulfillmentGroups.every((fg) => {
      if (
        fg.status !== FulfillmentGroupStatus.Open &&
        fg.status !== FulfillmentGroupStatus.OnHold
      ) {
        return false;
      }

      if (fg.providers?.length !== 1) {
        return false;
      }

      const [provider] = fg.providers;
      return provider != null && CANCELABLE_ORDER_PROVIDERS.has(provider);
    })
  );
}

export function useAllowedActions(
  fulfillmentGroups: readonly FulfillmentGroupData[],
) {
  const team = useRequiredContext(TeamContext);

  // Check if we can notify marketplace (single group with pending or skipped status)
  const canNotifyMarketplace = useMemo(() => {
    if (fulfillmentGroups.length !== 1) {
      return false;
    }
    return (
      fulfillmentGroups[0]?.marketplaceNotification?.status === "skipped" ||
      fulfillmentGroups[0]?.marketplaceNotification?.status === "failed" ||
      (fulfillmentGroups[0]?.marketplaceNotification?.status === "pending" &&
        fulfillmentGroups[0]?.marketplaceNotification?.date != null)
    );
  }, [fulfillmentGroups]);

  const canHoldEveryGroup = useMemo(() => {
    return fulfillmentGroups.every((fg) => fg.canPlaceHold);
  }, [fulfillmentGroups]);

  const canSubmitCarrierClaim = useMemo(() => {
    if (fulfillmentGroups.length !== 1) {
      return false;
    }
    return !!getEffectiveShipmentSummary(fulfillmentGroups[0])
      ?.claimActionSummary.claimable;
  }, [fulfillmentGroups]);

  // Blocked only by the merchant's own carrier account below the insurance
  // threshold — surface as disabled-with-tooltip instead of hiding it.
  const carrierClaimMerchantAccountBlocked = useMemo(() => {
    if (fulfillmentGroups.length !== 1 || canSubmitCarrierClaim) {
      return false;
    }
    const claimAction = getEffectiveShipmentSummary(
      fulfillmentGroups[0],
    )?.claimActionSummary;
    return (
      claimAction?.claimable === false &&
      claimAction.reason === UnclaimableReason.IS_MERCHANT_ACCOUNT
    );
  }, [fulfillmentGroups, canSubmitCarrierClaim]);

  // Claim IDs already submitted for this single-group view, so we can show a
  // "Carrier claim submitted on X" entry in the actions dropdown that links to
  // the existing claim instead of silently hiding the option.
  const submittedClaimIds = useMemo(() => {
    if (fulfillmentGroups.length !== 1) {
      return [];
    }
    const fg = fulfillmentGroups[0]!;
    const byTracker = fg.shippingClaimIdsByTrackingNumber
      ? Object.values(fg.shippingClaimIdsByTrackingNumber).flat()
      : [];
    if (byTracker.length > 0) {
      return byTracker;
    }
    return fg.shippingClaimIds ?? [];
  }, [fulfillmentGroups]);

  const submittedClaimSummariesQuery = useRpcQuery(
    RedoOutboundLabelsRpcClientContext,
    "getMerchantShippingClaimSummariesByIds",
    { ids: submittedClaimIds },
    {
      enabled: !canSubmitCarrierClaim && submittedClaimIds.length > 0,
      staleTime: 5 * 60 * 1000,
    },
  );

  const submittedShippingClaim = useMemo<{
    id: string;
    submittedAt: Date;
  } | null>(() => {
    const summaries = submittedClaimSummariesQuery.data?.summaries;
    if (!summaries || summaries.length === 0) {
      return null;
    }
    const latest = summaries.reduce((acc, s) =>
      s.submittedAt > acc.submittedAt ? s : acc,
    );
    return { id: latest._id, submittedAt: latest.submittedAt };
  }, [submittedClaimSummariesQuery.data]);

  const canStartReturn = useMemo(() => {
    if (fulfillmentGroups.length !== 1) {
      return false;
    }

    const onlyOneGroupWithOneOrder =
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      fulfillmentGroups[0].orders.length === 1 &&
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      !!fulfillmentGroups[0].orders[0].redoOrderId;

    // For now only let them start returns for Shopify
    const isShopify =
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      fulfillmentGroups[0].providers?.length === 1 &&
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      fulfillmentGroups[0].providers[0] === Provider.SHOPIFY;
    // Returns let's merchants go to the returns page for unfulfilled orders, so we do too
    const isOnReturns = team.settings?.returns?.enabled;
    return onlyOneGroupWithOneOrder && !!isOnReturns && isShopify;
  }, [fulfillmentGroups, team.settings]);

  // We should only edit manual orders for now
  const canEditManualOrder = useMemo(() => {
    return (
      fulfillmentGroups.length === 1 &&
      fulfillmentGroups[0]?.orderSources.length === 1 &&
      fulfillmentGroups[0].orderSources[0] === Provider.MANUAL
    );
  }, [fulfillmentGroups]);

  const canCancel = useMemo(() => {
    return canCancelFulfillmentGroups(fulfillmentGroups);
  }, [fulfillmentGroups]);

  const canCreateReturnLabel = useMemo(() => {
    if (fulfillmentGroups.length !== 1) {
      return false;
    }
    return (
      !fulfillmentGroups[0]?.returnLabelUrl &&
      !!summaryPackages(getEffectiveShipmentSummary(fulfillmentGroups[0]))
        .length &&
      fulfillmentGroups[0]?.selectedParcels?.length === 1
    );
  }, [fulfillmentGroups]);

  const canPrintReturnLabel = useMemo(() => {
    if (fulfillmentGroups.length !== 1) {
      return false;
    }
    return !!fulfillmentGroups[0]?.returnLabelUrl;
  }, [fulfillmentGroups]);

  return {
    canNotifyMarketplace,
    canHoldEveryGroup,
    canSubmitCarrierClaim,
    carrierClaimMerchantAccountBlocked,
    submittedShippingClaim,
    canStartReturn,
    canEditManualOrder,
    canCancel,
    canCreateReturnLabel,
    canPrintReturnLabel,
  };
}
